import Pusher from 'pusher-js';
class WebSocket {
    static instance = null;
    static bindBeforeInstanceInited = [];
    static bindStateChangeBeforeInstanceInited = [];

    constructor(host, port, appKey, channel, viewState = false) {
        this.host = host;
        this.appKey = appKey;
        this.port = port;
        this.socket = null;
        this.pusher = null;
        this.state = 'disconnected';
        this.channel = channel;
        this.pusherChannel = null;
        this.stateChangeBindings = [];
        this.viewState = viewState;

        /** @type {HTMLElement|null} */
        this.stateBadgeEl = null;

        // buffer pro skládání rozdělených zpráv: Map<uid, { total:number, parts: Array<string>, received:number, createdAt:number }>
        this.chunkBuffers = new Map();

        // TTL pro chunkové zprávy (20 minut)
        this.chunkTtlMs = 20 * 60 * 1000;

        // Periodické čištění starých bufferů
        this.chunkCleanupInterval = setInterval(() => this.cleanupChunkBuffers(), 60 * 1000);

        // pokud chceme zobrazovat stav ve view, připrav element (pokud je DOM ready)
        if (this.viewState && typeof window !== 'undefined') {
            if (document.readyState === 'loading') {
                window.addEventListener('DOMContentLoaded', () => this.ensureStateBadge());
            } else {
                this.ensureStateBadge();
            }
        }
    }

    static init(host, port, appKey, channel, viewState = false) {
        if (!WebSocket.instance) {
            WebSocket.instance = new WebSocket(host, port, appKey, channel, viewState);

            if (WebSocket.bindBeforeInstanceInited.length > 0) {
                WebSocket.bindBeforeInstanceInited.forEach(item => {
                    WebSocket.bind(item.name, item.callback);
                });
                WebSocket.bindBeforeInstanceInited = [];
            }
            if (WebSocket.bindStateChangeBeforeInstanceInited.length > 0) {
                WebSocket.bindStateChangeBeforeInstanceInited.forEach(item => {
                    WebSocket.bindStateChange(item.callback);
                });
                WebSocket.bindStateChangeBeforeInstanceInited = [];
            }
        }
        return WebSocket.instance;
    }

    static getInstance() {
        if (!WebSocket.instance) {
            throw new Error('WebSocket není inicializován. Nejdříve zavolejte WebSocket.init()');
        }
        return WebSocket.instance;
    }

    isConnected() {
        return this.state === 'connected';
    }

    getState() {
        return this.state;
    }

    connect() {
        if (this.pusher) {
            return;
        }


        this.pusher = new Pusher(this.appKey, {
            wsHost: this.host,
            wssHost: this.host,
            wsPort: this.port,
            wssPort: this.port,
            forceTLS: true,
            enabledTransports: ['ws', 'wss'],
            wsPath: '',
            cluster: 'mt1',
            authEndpoint: '/pusher/broadcasting/auth',
            enableLogging: true,
            logToConsole: true
        });

        // po vytvoření klienta případně aktualizujeme badge na "připojování"
        if (this.viewState) {
            this.updateStateBadge('connecting');
        }

        // Arrow funkce pro zachování this kontextu
        this.pusher.connection.bind('state_change', (states) => {
            const state = states.current;
            this.state = state;
            if (state === 'connected') {
                console.log("Připojení k Reverb serveru navázáno.");
            } else if (state === 'disconnected') {
                console.log("Připojení k Reverb serveru ztraceno.");
            } else {
                console.log("Stav spojení: " + state);
            }

            if (this.viewState) {
                this.updateStateBadge(state);
            }

            this.stateChangeBindings.forEach(cb => cb(state));
        });

        this.pusher.connection.bind('error', (err) => {
            if (err.error && err.error.data) {
                const message = err.error.data.message || 'Unknown error.';
                console.error("Chyba spojení: " + message);
            }
        });

        this.pusherChannel = this.pusher.subscribe(this.channel);
    }

    /**
     * Vytvoří obalovací callback, který umí složit rozdělené zprávy dle formátu
     * {uid,total,index,chunk} serializovaného jako JSON v data.message.
     * Pokud data.message není v tomto formátu, předá zprávu přímo dál.
     * @param {Function} originalCallback
     * @returns {Function}
     */
    static makeChunkAwareCallback(originalCallback) {
        const instance = WebSocket.getInstance();
        return (data) => {
            // průběžné čištění starých bufferů při každé příchozí zprávě
            instance.cleanupChunkBuffers();
            try {
                const msg = data && data.message;
                if (typeof msg !== 'string') {
                    // neočekávaný typ -> předáme dál beze změny
                    originalCallback(data);
                    return;
                }

                // pokus o parse JSONu z chunkované zprávy
                let parsed;
                try {
                    parsed = JSON.parse(msg);
                } catch (ignore) {
                    parsed = null;
                }

                const isChunk = !!(parsed && typeof parsed === 'object' &&
                    typeof parsed.uid === 'string' &&
                    Number.isInteger(parsed.total) && parsed.total > 0 &&
                    Number.isInteger(parsed.index) && parsed.index > 0 && parsed.index <= parsed.total &&
                    typeof parsed.chunk === 'string');

                if (!isChunk) {
                    // není to chunkovaná zpráva -> zavoláme callback rovnou
                    originalCallback(data);
                    return;
                }

                const { uid, total, index, chunk } = parsed;
                let buf = instance.chunkBuffers.get(uid);
                if (!buf) {
                    buf = { total: total, parts: new Array(total).fill(''), received: 0, createdAt: Date.now() };
                    instance.chunkBuffers.set(uid, buf);
                }

                // pokud by během přenosu došlo ke změně total, přizpůsobíme velikost
                if (buf.total !== total) {
                    const newParts = new Array(total).fill('');
                    for (let i = 0; i < Math.min(buf.parts.length, newParts.length); i++) {
                        newParts[i] = buf.parts[i] || '';
                    }
                    buf.parts = newParts;
                    buf.total = total;
                }

                const partIdx = index - 1;
                if (buf.parts[partIdx] === '') {
                    buf.received += 1;
                }
                buf.parts[partIdx] = chunk;

                if (buf.received >= buf.total) {
                    // kompletní zpráva složena
                    const reconstructed = buf.parts.join('');
                    instance.chunkBuffers.delete(uid);

                    // předáme dál s nahrazenou message
                    const forwarded = { ...data, message: reconstructed };
                    originalCallback(forwarded);
                }
                // pokud není kompletní, nevoláme callback
            } catch (err) {
                console.error('Chyba při zpracování WS zprávy:', err);
                // fallback: raději předat původní data, ať se neztratí
                originalCallback(data);
            }
        };
    }

    /**
     * Naváže callback na událost nebo události v WebSocket kanálu
     * @param {string|string[]} name - Název události nebo pole názvů událostí
     * @param {Function} callback - Callback funkce, která se zavolá při události
     */
    static bind(name, callback) {
        try {
            const instance = WebSocket.getInstance();
            if (!instance.pusher) {
                instance.connect();
            }

            const wrapped = WebSocket.makeChunkAwareCallback(callback);

            // pokud je name jako pole, tak foreachuj
            if (Array.isArray(name)) {
                name.forEach(n => {
                    instance.pusherChannel.bind(n, wrapped);
                });
            } else {
                instance.pusherChannel.bind(name, wrapped);
            }
        } catch (e) {
            console.log(e);
            // pokud je name pole, tak foreachuj
            if (Array.isArray(name)) {
                name.forEach(n => {
                    WebSocket.bindBeforeInstanceInited.push({ name: n, callback: callback }); // Uložíme callback pro pozdější použití
                });
            } else {
                WebSocket.bindBeforeInstanceInited.push({ name: name, callback: callback }); // Uložíme callback pro pozdější použití
            }
        }
    }

    static bindStateChange(callback) {
        try {
            const instance = WebSocket.getInstance();
            if (!instance.pusher) {
                instance.connect();
            }
            instance.stateChangeBindings.push(callback);
        } catch (e) {
            WebSocket.bindStateChangeBeforeInstanceInited.push({ callback: callback }); // Uložíme callback pro pozdější použití
        }
    }

    // Smaže rozpracované chunkové zprávy, které přesáhly TTL
    cleanupChunkBuffers() {
        try {
            const now = Date.now();
            /** @type {Array<string>} */
            const toDelete = [];
            this.chunkBuffers.forEach((buf, uid) => {
                const createdAt = typeof buf.createdAt === 'number' ? buf.createdAt : now;
                if (now - createdAt > this.chunkTtlMs) {
                    toDelete.push(uid);
                }
            });
            toDelete.forEach(uid => this.chunkBuffers.delete(uid));
        } catch (e) {
            // pouze log, ať nespadne klient kvůli úklidu
            console.warn('WS cleanupChunkBuffers error:', e);
        }
    }

    close() {
        if (this.chunkCleanupInterval) {
            clearInterval(this.chunkCleanupInterval);
            this.chunkCleanupInterval = null;
        }
        this.chunkBuffers.clear();
        if (this.pusher) {
            this.pusher.unsubscribe(this.channel);
            this.pusher.disconnect();
            this.pusher = null;
            this.state = 'disconnected';
        }
        if (this.viewState) {
            this.updateStateBadge('disconnected');
        }
    }

    /**
     * Zajistí existenci badge elementu ve spodní části primární navigace
     * a uloží referenci do this.stateBadgeEl.
     */
    ensureStateBadge() {
        try {
            const nav = document.querySelector('.Polaris-Navigation__PrimaryNavigation');
            if (!nav) {
                return;
            }
            // vytvoření a vložení indikátoru stavu jako běžného prvku na konci seznamu
            let el = nav.querySelector('.ws-state-indicator');
            if (!el) {
                el = document.createElement('div');
                el.className = 'ws-state-indicator';
                el.setAttribute('aria-live', 'polite');
                // pozicování přes normální tok dokumentu, nikoliv absolute
                el.style.position = 'static';
                el.style.margin = '8px';
                // větší mezera od horního obsahu
                el.style.marginTop = '16px';
                // přidání většího paddingu pro lepší čitelnost
                el.style.padding = '10px 12px';
                el.style.borderRadius = '6px';
                el.style.fontSize = '12px';
                el.style.display = 'flex';
                el.style.alignItems = 'center';
                el.style.gap = '6px';
                el.style.background = 'rgba(0,0,0,0.06)';
                el.style.backdropFilter = 'saturate(120%) blur(2px)';
                el.style.border = '1px solid rgba(0,0,0,0.08)';
                el.style.boxSizing = 'border-box';
                el.style.maxWidth = '100%';
                el.style.overflow = 'hidden';
                el.style.whiteSpace = 'nowrap';
                el.style.textOverflow = 'ellipsis';

                const dot = document.createElement('span');
                dot.className = 'ws-state-dot';
                dot.style.width = '8px';
                dot.style.height = '8px';
                dot.style.borderRadius = '50%';
                dot.style.display = 'inline-block';
                dot.style.background = '#999';

                const text = document.createElement('span');
                text.className = 'ws-state-text';
                text.textContent = 'WebSocket: připojování…';
                text.style.color = '#444';

                el.appendChild(dot);
                el.appendChild(text);
                nav.appendChild(el);
            }
            this.stateBadgeEl = el;
        } catch (e) {
            // ignore UI errors
        }
    }

    /**
     * Aktualizuje text a barvy indikátoru podle stavu.
     * @param {string} state
     */
    updateStateBadge(state) {
        try {
            if (!this.viewState) return;
            if (!this.stateBadgeEl) {
                this.ensureStateBadge();
            }
            const el = this.stateBadgeEl;
            if (!el) return;
            const dot = el.querySelector('.ws-state-dot');
            const text = el.querySelector('.ws-state-text');

            let color = '#999';
            let label = 'WebSocket: ' + (state || 'unknown');
            if (state === 'connected') {
                color = '#2E7D32';
                label = 'WebSocket: připojeno';
            } else if (state === 'connecting' || state === 'initialized') {
                color = '#F9A825';
                label = 'WebSocket: připojování…';
            } else if (state === 'disconnected' || state === 'unavailable') {
                color = '#C62828';
                label = 'WebSocket: odpojeno';
            }

            if (dot) {
                dot.style.background = color;
                dot.style.boxShadow = `0 0 0 3px ${color}22`;
            }
            if (text) {
                text.textContent = label;
            }
        } catch (e) {
            // ignore UI errors
        }
    }
}

export default WebSocket;
